/**
 * Servicio REST para Solicitudes de Reembolso del staff.
 *
 * Espejo de `letterOfGuaranteeService.js` con la misma forma (mismos
 * 12 métodos), pero apuntando al endpoint
 * `insurance/reimbursement/` del backend.
 *
 * Diferencias funcionales con Carta Aval:
 *  - Sin `medicalCenterSection`, sin `diagnosis` (el reembolso no
 *    tiene centro médico ni diagnóstico CIE-10).
 *  - El campo `observation` se reemplaza por `reason` (motivo del
 *    reembolso, obligatorio).
 *  - Sin campo `subsidiary` en el payload de create/update.
 *  - El archivo de aprobación se llama "Comprobante de reembolso"
 *    (vs "Carta Aval firmada").
 */
import { BASE_URL } from '../constants';
import { getAuthHeader } from '../utils';

const ENDPOINT = `${BASE_URL}insurance/reimbursement/`;

function buildQuery(params) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== '') qs.append(k, v);
  });
  return qs.toString();
}

async function ensureOk(res, defaultMsg) {
  if (!res.ok) {
    let data = {};
    try {
      data = await res.json();
    } catch {
      data = {};
    }
    const payload = data.error || data.detail || data.status;
    if (typeof payload === 'string' && payload) {
      throw new Error(payload);
    }
    // Errores por campo de DRF: { "holder": ["..."] } (llegan en el body
    // sin clave `detail`).
    const source = payload && typeof payload === 'object' ? payload : data;
    const fields = Object.entries(source || {})
      .map(([field, messages]) => [
        field,
        [].concat(messages).filter(message => typeof message === 'string'),
      ])
      .filter(([, messages]) => messages.length)
      .map(([field, messages]) => `${field}: ${messages.join(' ')}`)
      .join(' · ');
    throw new Error(fields || defaultMsg);
  }
  return res.json();
}

const JSON_HEADERS = () => ({
  'Content-Type': 'application/json',
  ...getAuthHeader(),
});

function buildJsonPayload(fields) {
  const out = {};
  Object.entries(fields).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    out[k] = v;
  });
  return JSON.stringify(out);
}

export const reimbursementService = {
  list: (params = {}) =>
    fetch(`${ENDPOINT}?${buildQuery(params)}`, {
      headers: getAuthHeader(),
    }).then(r => ensureOk(r, 'Error al listar reembolsos')),

  retrieve: id =>
    fetch(`${ENDPOINT}${id}/`, { headers: getAuthHeader() }).then(r =>
      ensureOk(r, 'Error al cargar el reembolso')
    ),

  /**
   * Crear solicitud de reembolso.
   * Mismos dos modos que Carta Aval: FormData legacy o JSON con
   * S3 keys (`documents_data: [{key, file_name, file_type, category}]`).
   * El campo `reason` es OBLIGATORIO (validado server-side).
   */
  create: payload => {
    if (payload instanceof FormData) {
      return fetch(ENDPOINT, {
        method: 'POST',
        headers: getAuthHeader(),
        body: payload,
      }).then(r => ensureOk(r, 'Error al crear el reembolso'));
    }
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: JSON_HEADERS(),
      body: buildJsonPayload(payload),
    }).then(r => ensureOk(r, 'Error al crear el reembolso'));
  },

  update: (id, payload) => {
    if (payload instanceof FormData) {
      return fetch(`${ENDPOINT}${id}/`, {
        method: 'PATCH',
        headers: getAuthHeader(),
        body: payload,
      }).then(r => ensureOk(r, 'Error al actualizar el reembolso'));
    }
    return fetch(`${ENDPOINT}${id}/`, {
      method: 'PATCH',
      headers: JSON_HEADERS(),
      body: buildJsonPayload(payload),
    }).then(r => ensureOk(r, 'Error al actualizar el reembolso'));
  },

  /**
   * Aprobar reembolso. observation + approvedAmount obligatorios.
   * El PDF firmado puede venir como approvalKey (S3) o approvalFile
   * (multipart legacy).
   */
  approve: (
    id,
    {
      observation = '',
      approvalFile = null,
      approvalKey = null,
      approvalFileName = null,
      approvedAmount = null,
    } = {}
  ) => {
    if (!observation || !observation.trim()) {
      throw new Error('Debe escribir el motivo de la aprobación.');
    }
    if (approvedAmount == null || Number(approvedAmount) <= 0) {
      throw new Error('Debe indicar el monto aprobado en USD.');
    }

    if (approvalKey) {
      return fetch(`${ENDPOINT}${id}/approve/`, {
        method: 'PATCH',
        headers: JSON_HEADERS(),
        body: JSON.stringify({
          observation,
          approved_amount: String(approvedAmount),
          approval_document_key: approvalKey,
          approval_document_name: approvalFileName || `reimbursement_approval_${id}.pdf`,
        }),
      }).then(r => ensureOk(r, 'Error al aprobar el reembolso'));
    }

    if (!approvalFile) {
      throw new Error('Debe adjuntar el comprobante firmado del seguro para aprobar.');
    }
    const fd = new FormData();
    fd.append('observation', observation);
    fd.append('approval_document', approvalFile);
    fd.append('approved_amount', String(approvedAmount));
    return fetch(`${ENDPOINT}${id}/approve/`, {
      method: 'PATCH',
      headers: getAuthHeader(),
      body: fd,
    }).then(r => ensureOk(r, 'Error al aprobar el reembolso'));
  },

  /**
   * Rechazar reembolso. observation obligatoria + PDF firmado.
   * Mismos dos modos que approve.
   */
  reject: (
    id,
    { observation = '', approvalFile = null, approvalKey = null, approvalFileName = null } = {}
  ) => {
    if (!observation || !observation.trim()) {
      throw new Error('Debe escribir el motivo del rechazo.');
    }

    if (approvalKey) {
      return fetch(`${ENDPOINT}${id}/reject/`, {
        method: 'PATCH',
        headers: JSON_HEADERS(),
        body: JSON.stringify({
          observation,
          approval_document_key: approvalKey,
          approval_document_name: approvalFileName || `reimbursement_rejection_${id}.pdf`,
        }),
      }).then(r => ensureOk(r, 'Error al rechazar el reembolso'));
    }

    if (!approvalFile) {
      throw new Error('Debe adjuntar el comprobante firmado del seguro para rechazar.');
    }
    const fd = new FormData();
    fd.append('observation', observation);
    fd.append('approval_document', approvalFile);
    return fetch(`${ENDPOINT}${id}/reject/`, {
      method: 'PATCH',
      headers: getAuthHeader(),
      body: fd,
    }).then(r => ensureOk(r, 'Error al rechazar el reembolso'));
  },

  /**
   * Cambiar el status a uno INTERNO. Solo MASTER.
   *
   * @param {string|number} id
   * @param {object} params
   * @param {'pendiente_documento'|'asesoria_medica'|'en_negociacion'} params.status
   * @param {string} params.observation motivo obligatorio
   * @param {string[]} [params.missingCategories] solo si status === pendiente_documento
   */
  setInternalStatus: (id, { status, observation, missingCategories = [] } = {}) => {
    if (!status) throw new Error('Debe indicar el status destino.');
    if (!observation || !observation.trim()) {
      throw new Error('Debe indicar el motivo del cambio.');
    }
    const body = { status, observation };
    if (
      status === 'pendiente_documento' &&
      Array.isArray(missingCategories) &&
      missingCategories.length > 0
    ) {
      body.missing_categories = missingCategories;
    }
    return fetch(`${ENDPOINT}${id}/set-internal-status/`, {
      method: 'PATCH',
      headers: JSON_HEADERS(),
      body: JSON.stringify(body),
    }).then(r => ensureOk(r, 'Error al cambiar el status interno'));
  },

  /**
   * Marcar el reembolso como completado (status === entregado).
   * Roles permitidos: ADMIN, CALL_CENTER. Solo si status === aprobado.
   */
  markDelivered: id =>
    fetch(`${ENDPOINT}${id}/mark-delivered/`, {
      method: 'PATCH',
      headers: JSON_HEADERS(),
      body: JSON.stringify({}),
    }).then(r => ensureOk(r, 'Error al marcar como reembolsado')),

  /**
   * Agregar un documento (soporte) a un reembolso existente.
   * Mismos dos modos que Carta Aval: S3 (`key`, `file_name`,
   * `file_type`, `category`) o legacy multipart.
   */
  addDocument: (id, opts) => {
    if (typeof opts === 'string' || opts instanceof File) {
      const file = opts;
      const category = arguments[2];
      if (!file) throw new Error('Debe adjuntar un archivo PDF.');
      if (!category) throw new Error('Debe indicar la categoría del documento.');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', category);
      return fetch(`${ENDPOINT}${id}/add_document/`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: fd,
      }).then(r => ensureOk(r, 'Error al agregar el documento'));
    }
    const { key, file_name, file_type, category } = opts || {};
    if (!key) throw new Error('Debe proporcionar la key de S3 del archivo.');
    if (!category) throw new Error('Debe indicar la categoría del documento.');
    return fetch(`${ENDPOINT}${id}/add_document/`, {
      method: 'POST',
      headers: JSON_HEADERS(),
      body: JSON.stringify({
        key,
        file_name: file_name || `document_${id}.pdf`,
        file_type: file_type || 'application/pdf',
        category,
      }),
    }).then(r => ensureOk(r, 'Error al agregar el documento'));
  },

  /**
   * Historial / trazabilidad de un reembolso. Combina cambios en la
   * solicitud + documentos asociados, ordenados descendente.
   * Solo accesible para MASTER/ADMIN/CALL_CENTER (backend valida).
   */
  history: async id => {
    const res = await fetch(`${ENDPOINT}${id}/history/`, {
      headers: getAuthHeader(),
    });
    const text = await res.text();
    let payload = null;
    try {
      payload = text ? JSON.parse(text) : null;
    } catch {
      if (!res.ok) {
        throw new Error(
          `Endpoint no disponible (HTTP ${res.status}). Verifica que el backend tenga el merge con el commit del historial.`
        );
      }
      throw new Error('Respuesta inesperada del servidor.');
    }
    if (!res.ok) {
      throw new Error(
        payload?.error || payload?.detail || `Error al cargar el historial (HTTP ${res.status})`
      );
    }
    return payload;
  },

  /**
   * Lista las notas humanas (bitácora) de un reembolso, en orden
   * descendente. Solo accesible para roles internos.
   */
  listNotes: async id => {
    const res = await fetch(`${ENDPOINT}${id}/notes/`, {
      headers: getAuthHeader(),
    });
    const text = await res.text();
    let payload = null;
    try {
      payload = text ? JSON.parse(text) : null;
    } catch {
      if (!res.ok) {
        throw new Error(`Endpoint de notas no disponible (HTTP ${res.status}).`);
      }
      throw new Error('Respuesta inesperada del servidor.');
    }
    if (!res.ok) {
      throw new Error(
        payload?.error || payload?.detail || `Error al cargar la bitácora (HTTP ${res.status})`
      );
    }
    return payload;
  },

  /**
   * Crea una nueva nota en la bitácora. La nota es inmutable.
   */
  addNote: async (id, body) => {
    const res = await fetch(`${ENDPOINT}${id}/notes/`, {
      method: 'POST',
      headers: { ...JSON_HEADERS(), ...getAuthHeader() },
      body: JSON.stringify({ body }),
    });
    return ensureOk(res, 'Error al agregar la nota');
  },
};
